import { useState, useEffect, useRef } from 'react'
import { FORM_ERROR_TEXT_CLASS } from '@/components/ui/Tone'

interface RefundsInputProps {
  label: string
  value?: number
  onChange: (value: number | undefined) => void
  disabled?: boolean
  onFocus?: () => void
  onBlur?: () => void
}

export function RefundsInput({ 
  label, 
  value, 
  onChange, 
  disabled = false,
  onFocus: externalOnFocus,
  onBlur: externalOnBlur
}: RefundsInputProps) {
  const [displayValue, setDisplayValue] = useState<string>('')
  const [isFocused, setIsFocused] = useState(false)
  const [formulaError, setFormulaError] = useState<string>('')
  const inputRef = useRef<HTMLInputElement>(null)
  const normalizeDecimalSeparator = (raw: string) => raw.replace(/,/g, '.')

  // Обновляем отображаемое значение при изменении value
  useEffect(() => {
    if (!isFocused) {
      if (value !== undefined && value !== null) {
        // Всегда показываем как положительное число в поле ввода
        setDisplayValue(Math.abs(value).toString())
      } else {
        setDisplayValue('')
      }
    }
  }, [value, isFocused])

  const evaluateFormula = (formula: string): number | null => {
    try {
      // Удаляем знак "=" в начале
      const expression = normalizeDecimalSeparator(formula.slice(1).trim())
      
      // Проверяем, что выражение содержит только разрешенные символы
      if (!/^[\d+\-*/().\s]+$/.test(expression)) {
        throw new Error('Недопустимые символы')
      }
      
      // Вычисляем выражение
      // eslint-disable-next-line no-eval
      const result = eval(expression)
      
      if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
        throw new Error('Некорректный результат')
      }
      
      return result
    } catch (error) {
      return null
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
    setFormulaError('')
    // Если поле пустое, добавляем минус
    if (!displayValue || displayValue === '0') {
      setDisplayValue('')
    }
    // Вызываем внешний обработчик
    externalOnFocus?.()
  }

  const handleBlur = () => {
    setIsFocused(false)
    
    // Если введена формула, вычисляем её
    if (displayValue.startsWith('=')) {
      const result = evaluateFormula(displayValue)
      if (result !== null) {
        const positiveResult = Math.abs(result)
        setDisplayValue(positiveResult.toString())
        setFormulaError('')
        // Сохраняем как отрицательное значение
        onChange(-positiveResult)
      } else {
        setFormulaError('Ошибка в формуле')
        // Возвращаем к предыдущему значению
        if (value !== undefined && value !== null) {
          setDisplayValue(Math.abs(value).toString())
        } else {
          setDisplayValue('')
        }
      }
    } else {
      // При потере фокуса обрабатываем значение
      handleChange(displayValue)
    }
    
    // Вызываем внешний обработчик
    externalOnBlur?.()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value
    
    // Если начинается с "=", это формула
    if (inputValue.startsWith('=')) {
      setDisplayValue(inputValue)
      setFormulaError('')
      return
    }
    
    // Поддерживаем запятую как десятичный разделитель и нормализуем в точку
    inputValue = normalizeDecimalSeparator(inputValue)

    // Убираем все кроме цифр, точки и минуса
    inputValue = inputValue.replace(/[^\d.-]/g, '')
    
    // Убираем все минусы, если они есть (мы сами будем добавлять)
    inputValue = inputValue.replace(/-/g, '')
    
    // Ограничиваем количество точек
    const parts = inputValue.split('.')
    if (parts.length > 2) {
      inputValue = parts[0] + '.' + parts.slice(1).join('')
    }

    setDisplayValue(inputValue)
  }

  const handleChange = (inputValue: string) => {
    if (!inputValue || inputValue === '' || inputValue === '0') {
      onChange(undefined)
      return
    }

    const numericValue = parseFloat(normalizeDecimalSeparator(inputValue))
    if (isNaN(numericValue)) {
      onChange(undefined)
      return
    }

    // Всегда сохраняем как отрицательное число
    const negativeValue = numericValue > 0 ? -numericValue : numericValue
    onChange(negativeValue)
  }

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    // Предотвращаем изменение значения при прокрутке колесиком
    e.currentTarget.blur()
  }

  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-foreground">
        {label}
      </label>
      <div className="relative">
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground font-medium pointer-events-none">
          -
        </div>
        <input
          ref={inputRef}
          type="text"
          inputMode="decimal"
          value={displayValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onWheel={handleWheel}
          disabled={disabled}
          className={`input w-full pl-8 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        />
      </div>
      <div className="flex flex-col gap-1">
        <p className="text-xs text-muted-foreground">
          Сумма возврата (автоматически сохраняется с минусом)
        </p>
        {formulaError && (
          <div className={`text-xs ${FORM_ERROR_TEXT_CLASS}`}>{formulaError}</div>
        )}
        {isFocused && displayValue.startsWith('=') && (
          <div className="text-xs text-muted-foreground">
            💡 Формула будет вычислена после завершения ввода
          </div>
        )}
      </div>
    </div>
  )
}
